import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Spinner, Dropdown, Form } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CurrencyApi from '../../api/CurrencyApi';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPercentFieldValue,
  formatCalculatedFieldForDisplay,
  getCurrencyCodeFromRecord,
  resolveTokenCurrency,
} from '../../utils/formatUtils';
import {
  getListViewCellSortComparable,
  applySortDirection,
  buildExchangeRateLookupFromCurrencyList,
  resolveEffectiveExchangeRate,
  convertAmountToBase,
  getRollupOutputType,
} from '../../utils/reportCurrencyAggregate';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import { resolvePicklistColumnKey } from '../../utils/picklistValueUtils';
import MultiSelectDropdown from './MultiSelectDropdown1';
import './css/KanbanView.css';

const KanbanView = ({
  records,
  kanbanConfig,
  objectName,
  onUpdateRecord,
  loading,
  picklistOptions, // Pass picklist options for the groupBy field
  onEdit,           // Callback for edit action
  onDelete,         // Callback for delete action
  onClone,          // Callback for clone action
  onView            // Callback for view action
}) => {
  const [columns, setColumns] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectedStageFilters, setSelectedStageFilters] = useState([]);
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);

  // Base currency + exchange rates (same as ResizableTable / list view)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const raw = res?.data?.data ?? res?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const baseRow = list.find((r) => r.is_base_currency === true);
        setBaseCurrencyCode(baseRow?.currency_code ? String(baseRow.currency_code).trim() : null);
        setExchangeRateLookup(buildExchangeRateLookupFromCurrencyList(list));
      } catch (e) {
        if (!cancelled) {
          setBaseCurrencyCode(null);
          setExchangeRateLookup(null);
        }
      }
      try {
        const s = await getMultiCurrencySettings();
        if (!cancelled) setMultiCurrencyEnabled(!!s.multiCurrencyEnabled);
      } catch {
        if (!cancelled) setMultiCurrencyEnabled(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const unwrapScalarFromRecord = (val) => {
    if (val == null || val === '') return null;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      const inner = val.value ?? val.displayValue;
      if (inner == null || inner === '') return null;
      if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
        return inner.value ?? inner.displayValue ?? null;
      }
      return inner;
    }
    return val;
  };

  const getRecordExchangeRate = (record) => {
    if (!record) return null;
    const prefixedKey = objectName ? `${objectName}_exchange_rate` : null;
    if (prefixedKey && record[prefixedKey] != null) {
      const v = unwrapScalarFromRecord(record[prefixedKey]);
      if (v != null && v !== '') return v;
    }
    const er = record.exchange_rate ?? record.exchangeRate;
    const top = unwrapScalarFromRecord(er);
    if (top != null && top !== '') return top;
    if (Array.isArray(record.fields)) {
      const f = record.fields.find(
        (x) => x && String(x.fieldName || x.field_name || '').toLowerCase() === 'exchange_rate'
      );
      if (f) {
        const v = unwrapScalarFromRecord(f.value ?? f.displayValue);
        if (v != null && v !== '') return v;
      }
    }
    return null;
  };

  const getRecordCurrency = (record) => {
    if (!record) return null;
    const code = getCurrencyCodeFromRecord(record, {
      objectName,
      fallbackCurrencyCode: null,
    });
    return code && String(code).trim() ? String(code).trim().toUpperCase() : null;
  };

  const isKanbanFieldCurrencyLike = (field) => {
    if (!field) return false;
    const fieldType = (field.type || field.dataType || '').toLowerCase();
    if (fieldType === 'currency') return true;
    if (fieldType === 'rollupsummary') {
      return getRollupOutputType({
        data_type: 'rollupsummary',
        display_config: field.displayConfig || field.display_config,
        displayConfig: field.displayConfig || field.display_config,
      }) === 'currency';
    }
    return false;
  };

  const appendBaseCurrencyEquivalent = (formattedMain, numValue, record, recordCurrency, decimalPlaces) => {
    if (!multiCurrencyEnabled) return formattedMain;
    if (!record || !baseCurrencyCode || !recordCurrency) return formattedMain;
    if (
      String(recordCurrency).trim().toUpperCase() === String(baseCurrencyCode).trim().toUpperCase()
    ) {
      return formattedMain;
    }
    const rawExchangeRate = getRecordExchangeRate(record);
    const rowRate = Number(rawExchangeRate);
    const effectiveRate = resolveEffectiveExchangeRate(
      recordCurrency,
      rowRate,
      exchangeRateLookup
    );
    if (!Number.isFinite(effectiveRate) || effectiveRate <= 0) return formattedMain;
    const opts = {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };
    const baseEquivalent = numValue / effectiveRate;
    return `${formattedMain} (${formatCurrency(baseEquivalent, baseCurrencyCode, opts)} ${baseCurrencyCode})`;
  };

  const formatKanbanFieldValue = (field, record) => {
    if (
      !field ||
      field.value === null ||
      field.value === undefined ||
      field.value === ''
    ) {
      return null;
    }

    const fieldType = (field.type || field.dataType || '').toLowerCase();

    if (fieldType === 'calculated') {
      return formatCalculatedFieldForDisplay(field, record, { objectName });
    }

    if (fieldType === 'checkbox' || fieldType === 'boolean') {
      return field.value === true || field.value === 'true' || field.value === 1 ? 'Yes' : 'No';
    }

    if (fieldType === 'currency') {
      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        const decimalPlaces =
          field.scale !== undefined && field.scale !== null && field.scale !== ''
            ? parseInt(field.scale, 10)
            : 2;
        const recordCurrency = record ? getRecordCurrency(record) : null;
        const currencyCode = recordCurrency || field.currencyCode || resolveTokenCurrency('INR');
        const formatted = formatCurrency(numValue, currencyCode, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
        return appendBaseCurrencyEquivalent(formatted, numValue, record, recordCurrency, decimalPlaces);
      }
    }

    if (fieldType === 'percent') {
      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        return formatPercentFieldValue(numValue, field);
      }
    }

    if (fieldType === 'rollupsummary') {
      const displayConfig = field.displayConfig || field.display_config;
      let parsedConfig = {};
      if (displayConfig) {
        if (typeof displayConfig === 'string') {
          try {
            parsedConfig = JSON.parse(displayConfig);
          } catch (e) {
            parsedConfig = {};
          }
        } else {
          parsedConfig = displayConfig || {};
        }
      }
      const rollupConfig = parsedConfig.rollupSummary || {};
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();
      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        if (outputType === 'currency') {
          const decimalPlaces =
            field.scale !== undefined && field.scale !== null && field.scale !== ''
              ? parseInt(field.scale, 10)
              : 2;
          const recordCurrency = record ? getRecordCurrency(record) : null;
          const currencyCode = recordCurrency || field.currencyCode || resolveTokenCurrency('INR');
          const formatted = formatCurrency(numValue, currencyCode, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          });
          return appendBaseCurrencyEquivalent(formatted, numValue, record, recordCurrency, decimalPlaces);
        }
        if (outputType === 'percent') {
          return formatPercentFieldValue(numValue, field);
        }
        if (outputType === 'date') {
          try {
            const date = new Date(field.value);
            if (!isNaN(date.getTime())) return formatDate(field.value);
          } catch (e) {
            /* ignore */
          }
        }
        if (outputType === 'datetime') {
          try {
            const datetime = new Date(field.value);
            if (!isNaN(datetime.getTime())) return formatDateTime(field.value);
          } catch (e) {
            /* ignore */
          }
        }
        if (field.scale !== undefined && field.scale !== null && field.scale !== '') {
          const decimalPlaces = parseInt(field.scale, 10);
          return numValue.toLocaleString('en-IN', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          });
        }
        return numValue.toLocaleString('en-IN');
      }
    }

    if (fieldType === 'number' || fieldType === 'integer' || fieldType === 'decimal') {
      const numValue = Number(field.value);
      if (!isNaN(numValue)) {
        if (field.scale !== undefined && field.scale !== null && field.scale !== '') {
          const decimalPlaces = parseInt(field.scale, 10);
          return numValue.toLocaleString('en-IN', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          });
        }
        return numValue.toLocaleString('en-IN');
      }
    }

    if (fieldType === 'date' && field.value) {
      try {
        const date = new Date(field.value);
        if (!isNaN(date.getTime())) return formatDate(field.value);
      } catch (e) {
        /* ignore */
      }
    }

    if (fieldType === 'datetime' && field.value) {
      try {
        const datetime = new Date(field.value);
        if (!isNaN(datetime.getTime())) return formatDateTime(field.value);
      } catch (e) {
        /* ignore */
      }
    }

    return field.displayValue ?? String(field.value);
  };

  const convertRecordAmountToBase = (record, field, numValue) => {
    if (!isKanbanFieldCurrencyLike(field)) return numValue;
    if (!multiCurrencyEnabled || !baseCurrencyCode) return numValue;
    const currency = getRecordCurrency(record);
    const rowRate = Number(getRecordExchangeRate(record));
    const effectiveRate = resolveEffectiveExchangeRate(
      currency,
      rowRate,
      exchangeRateLookup
    );
    return convertAmountToBase(numValue, currency, effectiveRate, baseCurrencyCode);
  };

  // Group records by the kanban field
  useEffect(() => {
    if (!records || !kanbanConfig?.groupByField) return;

    const groupedRecords = {};

    // Initialize columns from ALL picklist options in the correct sort order
    // This ensures all values show up as columns, even if they have 0 records
    if (picklistOptions && picklistOptions.length > 0) {
      // Picklist options are already sorted by sort_order from the backend
      picklistOptions.forEach(option => {
        const valueKey = option.key || option.value;
        groupedRecords[valueKey] = {
          label: option.label || option.value,
          value: valueKey,
          records: [],
          color: option.color || '#6c757d',
          sortOrder: option.sort_order || 0
        };
      });
    }

    // Group records into the initialized columns (case-insensitive picklist match)
    records.forEach(record => {
      const fieldValue = record[kanbanConfig.groupByField]?.value;
      if (!fieldValue) {
        return;
      }

      const columnKey =
        resolvePicklistColumnKey(picklistOptions, fieldValue) || fieldValue;

      // Only add to columns if the value exists in picklist options
      // This prevents "uncategorized" columns for invalid/deleted values
      if (groupedRecords[columnKey]) {
        groupedRecords[columnKey].records.push(record);
      }
    });

    // Sort records within each column if configured
    if (kanbanConfig.sortBy) {
      Object.keys(groupedRecords).forEach(key => {
        groupedRecords[key].records.sort((a, b) => {
          const sortField = kanbanConfig.sortBy;
          const aCell = a[sortField];
          const bCell = b[sortField];
          const aVal = getListViewCellSortComparable(
            aCell,
            aCell?.type,
            aCell?.displayConfig ?? aCell?.display_config,
            sortField,
            null
          );
          const bVal = getListViewCellSortComparable(
            bCell,
            bCell?.type,
            bCell?.displayConfig ?? bCell?.display_config,
            sortField,
            null
          );
          return applySortDirection(kanbanConfig.sortOrder, aVal, bVal);
        });
      });
    }

    console.log('Kanban columns initialized:', {
      totalColumns: Object.keys(groupedRecords).length,
      columns: Object.keys(groupedRecords).map(key => ({
        key,
        label: groupedRecords[key].label,
        sortOrder: groupedRecords[key].sortOrder,
        recordCount: groupedRecords[key].records.length
      }))
    });

    setColumns(groupedRecords);
  }, [records, kanbanConfig, picklistOptions]);

  // Filter columns based on selected stage filters
  const filteredColumns = useMemo(() => {
    if (!selectedStageFilters || selectedStageFilters.length === 0) {
      return columns;
    }

    const filtered = {};
    Object.keys(columns).forEach(key => {
      if (selectedStageFilters.includes(key)) {
        filtered[key] = columns[key];
      }
    });
    return filtered;
  }, [columns, selectedStageFilters]);

  // Transform picklist options for MultiSelectDropdown
  const transformedPicklistOptions = useMemo(() => {
    if (!picklistOptions || picklistOptions.length === 0) return [];
    return picklistOptions.map(option => ({
      key: option.key || option.value,
      value: option.label || option.value
    }));
  }, [picklistOptions]);

  // Initialize selected filters with all values when picklist options change
  useEffect(() => {
    if (transformedPicklistOptions.length > 0 && selectedStageFilters.length === 0) {
      // Select all by default
      setSelectedStageFilters(transformedPicklistOptions.map(opt => opt.key));
    }
  }, [transformedPicklistOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Close any open dropdowns when overlay appears
  useEffect(() => {
    if (isUpdating && activeDropdown) {
      setActiveDropdown(null);
    }
  }, [isUpdating]);

  // Handle dropdown toggle
  const handleDropdownToggle = (recordId, isOpen) => {
    console.log('Dropdown toggle:', recordId, isOpen);
    if (isOpen) {
      setActiveDropdown(recordId);
    } else {
      setActiveDropdown(null);
    }
  };

  // Action handlers
  const handleViewRecord = (record) => {
    const recordIdValue = record.id?.value || record.id;
    if (onView) {
      onView(record);
    } else if (recordIdValue) {
      window.open(`/view/${objectName}/${recordIdValue}`, '_blank');
    }
  };

  const handleEditRecord = (record) => {
    if (onEdit) {
      onEdit(record);
    }
  };

  const handleCloneRecord = (record) => {
    if (onClone) {
      onClone(record);
    }
  };

  const handleDeleteRecord = (record) => {
    if (onDelete) {
      onDelete(record);
    }
  };

  // Handle drag and drop
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside the list
    if (!destination) return;

    // No movement
    if (source.droppableId === destination.droppableId &&
      source.index === destination.index) {
      return;
    }

    // Update record status
    setIsUpdating(true);
    try {
      const recordId = draggableId;
      const newStatus = destination.droppableId;

      // Call API to update the record
      await onUpdateRecord(recordId, {
        [kanbanConfig.groupByField]: newStatus
      });

      // Update local state optimistically
      const newColumns = { ...columns };
      const sourceColumn = { ...newColumns[source.droppableId] };
      const destColumn = { ...newColumns[destination.droppableId] };

      // Create new arrays to avoid mutation
      sourceColumn.records = [...sourceColumn.records];
      destColumn.records = [...destColumn.records];

      // Remove from source
      const [movedRecord] = sourceColumn.records.splice(source.index, 1);

      // Add to destination
      destColumn.records.splice(destination.index, 0, movedRecord);

      // Update the record's field value
      movedRecord[kanbanConfig.groupByField] = {
        ...movedRecord[kanbanConfig.groupByField],
        value: newStatus,
        displayValue: destColumn.label
      };

      // Update columns
      newColumns[source.droppableId] = sourceColumn;
      newColumns[destination.droppableId] = destColumn;

      setColumns(newColumns);
    } catch (error) {
      console.error('Error updating record:', error);
      // Refresh to get correct state
      window.location.reload();
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to get color based on field value
  const getColorForValue = (record, fieldName) => {
    if (!fieldName || !record[fieldName]) return '#6c757d';

    const value = record[fieldName].value;
    if (!value) return '#6c757d';

    const valueStr = String(value).toLowerCase();

    // Priority colors
    const priorityColors = {
      'high': '#dc3545',
      'critical': '#dc3545',
      'medium': '#ffc107',
      'normal': '#28a745',
      'low': '#28a745'
    };

    // Status colors
    const statusColors = {
      'new': '#17a2b8',
      'open': '#17a2b8',
      'in_progress': '#007bff',
      'in progress': '#007bff',
      'pending': '#ffc107',
      'completed': '#28a745',
      'closed': '#28a745',
      'cancelled': '#6c757d',
      'canceled': '#6c757d'
    };

    // Check priority colors first
    if (priorityColors[valueStr]) return priorityColors[valueStr];

    // Check status colors
    if (statusColors[valueStr]) return statusColors[valueStr];

    // For numeric values (like amounts), use gradient
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (numValue > 100000) return '#28a745'; // Green
      if (numValue > 50000) return '#ffc107';  // Yellow
      if (numValue > 10000) return '#17a2b8';  // Blue
      return '#6c757d'; // Gray
    }

    return '#6c757d'; // Default gray
  };

  // Render a single card
  const renderCard = (record, index) => {
    const cardFields = kanbanConfig.cardFields || [];
    const colorField = kanbanConfig.colorField;

    // Determine card color based on colorField
    const cardColor = getColorForValue(record, colorField);

    // Get the record ID for hyperlink
    const recordIdValue = record.id?.value || record.id;

    // Determine the title field and value
    const titleValue = record.name?.displayValue || record.name?.value ||
      record.subject?.displayValue || record.subject?.value || 'Untitled';

    // Check if we have a name or subject field (for hyperlink)
    const hasNameField = record.name !== undefined;
    const hasSubjectField = record.subject !== undefined;

    return (
      <Draggable key={recordIdValue} draggableId={recordIdValue} index={index}>
        {(provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`kanban-card mb-2 ${snapshot.isDragging ? 'dragging' : ''} ${activeDropdown === recordIdValue ? 'dropdown-active' : ''}`}
            style={{
              ...provided.draggableProps.style,
              borderLeft: `4px solid ${cardColor}`,
              opacity: snapshot.isDragging ? 0.9 : 1,
              zIndex: snapshot.isDragging ? 1000 : (activeDropdown === recordIdValue ? 1001 : 'auto'),
              position: 'relative',
              cursor: snapshot.isDragging ? 'grabbing' : 'grab'
            }}
          >
            <Card.Body
              className="p-3"
              style={{ position: 'relative' }}
            >
              {/* Actions Dropdown - Top Right Corner */}
              <div
                className="kanban-card-actions"
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent drag when clicking dropdown area
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent drag when clicking dropdown
                }}
                style={{
                  display: isUpdating ? 'none' : 'block' // Hide dropdown arrow when overlay is active
                }}
              >
                <Dropdown
                  onToggle={(isOpen) => handleDropdownToggle(recordIdValue, isOpen)}
                  show={activeDropdown === recordIdValue}
                  className="kanban-actions-dropdown"
                >
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    id={`kanban-dropdown-${recordIdValue}`}
                    className="kanban-card-dropdown-toggle"
                    style={{
                      padding: '2px 8px',
                      fontSize: '14px',
                      border: '1px solid #dee2e6',
                      backgroundColor: 'white',
                      color: '#6c757d',
                      lineHeight: 1
                    }}
                  >
                    <span style={{ color: '#6c757d', fontWeight: 'bold' }}>&#9660;</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end" className="kanban-dropdown-menu">
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleViewRecord(record);
                      }}
                    >
                      <i className="fas fa-eye me-2"></i>
                      View
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleEditRecord(record);
                      }}
                    >
                      <i className="fas fa-edit me-2"></i>
                      Edit
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleCloneRecord(record);
                      }}
                    >
                      <i className="fas fa-clone me-2"></i>
                      Clone
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleDeleteRecord(record);
                      }}
                      className="text-danger"
                    >
                      <i className="fas fa-trash me-2"></i>
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Card Title - clickable hyperlink to record detail page (like name field in table) */}
              <div className="kanban-card-title mb-2" style={{ paddingRight: '30px' }}>
                {recordIdValue && (hasNameField || hasSubjectField) ? (
                  <a
                    href={`/view/${objectName}/${recordIdValue}`}
                    className="table-link"
                    style={{
                      color: '#032d60',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent drag when clicking link
                    }}
                    onClick={(e) => {
                      // Allow navigation - don't stop propagation on click
                      // The mousedown stopPropagation is enough to prevent drag
                    }}
                  >
                    {titleValue}
                  </a>
                ) : (
                  <strong>{titleValue}</strong>
                )}
              </div>

              {/* Additional Fields - Only show fields that are in cardFields config */}
              {cardFields.map(fieldName => {
                // Skip name/subject as they're shown as title
                if (fieldName === 'name' || fieldName === 'subject') return null;

                // Skip id field - it will be handled separately only if configured
                if (fieldName === 'id') return null;

                if (!record[fieldName]) return null;

                const field = record[fieldName];
                const displayValue = formatKanbanFieldValue(field, record);

                // Skip if no value
                if (displayValue == null || displayValue === '') return null;

                return (
                  <div key={fieldName} className="kanban-card-field mb-1">
                    <small className="text-muted fw-bold">
                      {field.label}:
                    </small>
                    <span className="ms-1" style={{ fontSize: '13px' }}>
                      {displayValue}
                    </span>
                  </div>
                );
              })}

              {/* Record ID Badge - Only show if 'id' is in cardFields configuration */}
              {cardFields.includes('id') && (
                <div className="mt-2">
                  <Badge bg="light" text="dark" style={{ fontSize: '10px' }}>
                    ID: {record.id?.displayValue || record.id?.value || 'N/A'}
                  </Badge>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Draggable>
    );
  };

  // Calculate aggregation for summary fields
  const calculateAggregation = (records, fieldName, aggregation) => {
    // For count, always return 0 if no records, otherwise return count
    if (aggregation.toLowerCase() === 'count') {
      return records && records.length > 0 ? records.length : 0;
    }

    if (!records || records.length === 0) return 0;

    const sampleField = records[0]?.[fieldName];
    const aggregateInBase = isKanbanFieldCurrencyLike(sampleField);

    const values = records
      .map(record => {
        const field = record[fieldName];
        if (!field) return null;

        // Handle enriched data structure
        const value = field.value !== undefined ? field.value : field;
        if (value === null || value === undefined || value === '') return null;

        // Try to parse as number
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          return aggregateInBase
            ? convertRecordAmountToBase(record, field, numValue)
            : numValue;
        }

        // Try to parse as date
        const dateValue = new Date(value);
        if (!isNaN(dateValue.getTime())) return dateValue.getTime();

        return null;
      })
      .filter(val => val !== null);

    if (values.length === 0) return 0;

    switch (aggregation.toLowerCase()) {
      case 'sum':
        return values.reduce((sum, val) => sum + val, 0);
      case 'avg':
      case 'average':
        return values.reduce((sum, val) => sum + val, 0) / values.length;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
      case 'count':
        return records.length;
      default:
        return 0;
    }
  };

  // Check if a field is a currency field (aligned with ResizableTable)
  const isCurrencyField = (fieldName) => {
    if (!fieldName || !records || records.length === 0) return false;
    const field = records[0]?.[fieldName];
    return isKanbanFieldCurrencyLike(field);
  };

  // Check if a field is a date/datetime field
  const isDateField = (fieldName) => {
    if (!fieldName || !records || records.length === 0) return false;
    const field = records[0]?.[fieldName];
    if (!field) return false;

    // Check field type
    const fieldType = field.type?.toLowerCase();
    if (fieldType === 'date' || fieldType === 'datetime') return true;

    // Check dataType if available
    const dataType = field.dataType?.toLowerCase();
    if (dataType === 'date' || dataType === 'datetime') return true;

    return false;
  };

  // Format aggregated value for display
  const formatAggregatedValue = (value, fieldName, aggregation) => {
    const fieldMeta = records?.[0]?.[fieldName];
    const decimalPlaces =
      fieldMeta?.scale !== undefined && fieldMeta?.scale !== null && fieldMeta?.scale !== ''
        ? parseInt(fieldMeta.scale, 10)
        : 2;
    const fracOpts = {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };
    const summaryCurrencyCode =
      multiCurrencyEnabled && baseCurrencyCode && isCurrencyField(fieldName)
        ? baseCurrencyCode
        : resolveTokenCurrency('INR');

    // Show 0 instead of N/A for null/undefined values to maintain alignment
    if (value === null || value === undefined) {
      if (aggregation === 'count') return '0';
      // For numeric aggregations, return formatted 0
      if (isCurrencyField(fieldName)) {
        return formatCurrency(0, summaryCurrencyCode, fracOpts);
      }
      return '0';
    }

    if (aggregation === 'count') {
      return value.toString();
    }

    // Format as number
    if (typeof value === 'number') {
      // Check if it's a date field first (for min/max on dates)
      if ((aggregation === 'min' || aggregation === 'max') && isDateField(fieldName)) {
        // For date fields, the value might be a timestamp
        const dateValue = new Date(value);
        if (!isNaN(dateValue.getTime()) && dateValue.getTime() > 0) {
          return dateValue.toLocaleDateString();
        }
      }

      // Check if it's a currency field and use proper currency formatting
      if (isCurrencyField(fieldName)) {
        return formatCurrency(value, summaryCurrencyCode, fracOpts);
      }
      // Format with commas for large numbers
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value);
    }

    return value.toString();
  };

  // Render a column
  const renderColumn = (columnId, column) => {
    const recordCount = column.records.length;
    const showCount = kanbanConfig.showRecordCount !== false; // Default true
    const summaryFields = kanbanConfig.summaryFields || [];

    // Calculate summary totals for display at top
    const summaryTotals = summaryFields
      .filter(sf => sf.field && sf.aggregation)
      .map(summaryField => {
        const aggregatedValue = calculateAggregation(
          column.records,
          summaryField.field,
          summaryField.aggregation
        );
        return {
          ...summaryField,
          value: aggregatedValue,
          formattedValue: formatAggregatedValue(aggregatedValue, summaryField.field, summaryField.aggregation)
        };
      });

    return (
      <div key={columnId} className="kanban-column">
        <div className="kanban-column-header">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0">{column.label}</h6>
            {showCount && (
              <Badge bg="secondary" pill>{recordCount}</Badge>
            )}
          </div>
        </div>

        {/* Summary Totals - Below stage name, above cards */}
        {summaryTotals.length > 0 && (
          <div
            className="kanban-column-summary-totals"
            style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6'
            }}
          >
            {summaryTotals.map((summaryTotal, index) => {
              // For currency fields with sum aggregation, show prominently
              if (isCurrencyField(summaryTotal.field) && summaryTotal.aggregation === 'sum') {
                return (
                  <div
                    key={index}
                    className="kanban-summary-total-currency"
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#28a745',
                      marginBottom: index < summaryTotals.length - 1 ? '4px' : '0'
                    }}
                  >
                    {summaryTotal.formattedValue}
                  </div>
                );
              }
              // For other aggregations, show smaller
              return (
                <div
                  key={index}
                  className="kanban-summary-total-other"
                  style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    marginBottom: index < summaryTotals.length - 1 ? '2px' : '0'
                  }}
                >
                  <span style={{ fontWeight: '600' }}>
                    {column.records[0]?.[summaryTotal.field]?.label || summaryTotal.field} ({summaryTotal.aggregation === 'avg' ? 'Avg' : summaryTotal.aggregation === 'sum' ? 'Sum' : summaryTotal.aggregation === 'min' ? 'Min' : summaryTotal.aggregation === 'max' ? 'Max' : summaryTotal.aggregation}): {summaryTotal.formattedValue}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <Droppable droppableId={columnId}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`kanban-column-content ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
            >
              {column.records.map((record, index) => renderCard(record, index))}
              {provided.placeholder}

              {recordCount === 0 && !snapshot.isDraggingOver && (
                <div className="text-center text-muted p-4">
                  <i className="fas fa-inbox" style={{ fontSize: '32px', opacity: 0.3 }}></i>
                  <p className="mt-2 mb-0" style={{ fontSize: '13px' }}>No items</p>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading Kanban view...</p>
      </div>
    );
  }

  if (!kanbanConfig?.groupByField) {
    return (
      <div className="text-center p-5">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Kanban Configuration Missing</strong>
          <p className="mb-0 mt-2">
            Please configure a "Group By" field in the list view settings to enable Kanban view.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Stage Filter - Multi-select dropdown - Outside scrollable area */}
      {kanbanConfig?.groupByField && transformedPicklistOptions.length > 0 && (
        <div className="kanban-stage-filter" style={{
          padding: '12px 20px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}>
          <label style={{
            fontWeight: '600',
            color: '#032d60',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            margin: 0,
            flexShrink: 0
          }}>
            Filter Stages:
          </label>
          <div style={{ minWidth: '250px', maxWidth: '400px' }}>
            <MultiSelectDropdown
              selectedValues={selectedStageFilters}
              picklistValues={transformedPicklistOptions}
              onChange={setSelectedStageFilters}
              placeholder="Select stages to view..."
              disabled={loading}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setSelectedStageFilters(transformedPicklistOptions.map(opt => opt.key));
            }}
            disabled={loading || selectedStageFilters.length === transformedPicklistOptions.length}
            style={{
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Show All
          </button>
          {selectedStageFilters.length > 0 && (
            <Badge bg="info" style={{ fontSize: '12px', padding: '4px 8px', flexShrink: 0 }}>
              {selectedStageFilters.length} of {transformedPicklistOptions.length} selected
            </Badge>
          )}
        </div>
      )}

      <div className="kanban-view">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="kanban-board">
            {/* Render filtered columns in sort order from picklist configuration */}
            {Object.entries(filteredColumns)
              .sort((a, b) => (a[1].sortOrder || 0) - (b[1].sortOrder || 0))
              .map(([columnId, column]) =>
                renderColumn(columnId, column)
              )}

            {isUpdating && (
              <div className="kanban-updating-overlay">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-white fw-bold">Updating...</p>
              </div>
            )}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
};

export default KanbanView;
